// Cadastro de variável que ainda não existe no catálogo.
//
// A liberdade é real (o vocabulário é lista ABERTA), mas tem preço: declarar
// grandeza, unidade e FAIXA FÍSICA. Sem essa exigência o nome novo nasce igual
// aos que estragaram a base — DirVento com 374°, VelVento negativa, NivelBat
// com 131,25 V. Todos passaram porque não havia faixa.
//
// A grandeza é o que faz duas estações serem comparáveis; o nome é só o rótulo
// local. Por isso a grandeza é lista fechada e o nome não.
import { useMemo, useState } from 'react';
import { App as AntApp, Modal, Form, Input, InputNumber, Select, Switch, Alert, Typography, Space } from 'antd';
import { api } from './shared';

const { Text } = Typography;

export type Grandeza = {
  codigo: string; rotulo: string; unidade_canonica: string; natureza: string;
  limite_min: number | null; limite_max: number | null;
};
export type Variavel = {
  codigo: string; grandeza_codigo: string; rotulo: string; unidade: string;
  faixa_min: number | null; faixa_max: number | null; resolucao: number | null;
  acumulada: boolean; intervalo_seg: number | null; diagnostico: boolean;
  status: string; observacao: string | null;
};

// Mesma regra do banco (coluna codigo_norm) e do backend: minúsculo, sem acento,
// sem separador. É o que impede Nivel/nivel/NIVEL/Nível virarem quatro variáveis
// — que foi exatamente o que aconteceu entre a telemetria e a leitura manual.
export function normCodigo(s: string) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function ModalVariavelNova({ aberto, grandezas, variaveis, nomeInicial, onFechar, onCriada }: {
  aberto: boolean;
  grandezas: Grandeza[];
  variaveis: Variavel[];
  nomeInicial?: string;
  onFechar: () => void;
  onCriada: (v: Variavel) => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  const [codigo, setCodigo] = useState(nomeInicial || '');
  const [grandeza, setGrandeza] = useState<string | undefined>(undefined);

  const g = grandezas.find(x => x.codigo === grandeza);
  const semFaixa = grandeza === 'posicao' || grandeza === 'estado';
  const acumulavel = g?.natureza === 'acumulada';

  // Colisão é por nome NORMALIZADO, não por igualdade literal: "Nível" e "nivel"
  // colidem. Avisar antes de salvar evita o 409 seco do servidor.
  const colisao = useMemo(() => {
    const n = normCodigo(codigo);
    return n ? variaveis.find(v => normCodigo(v.codigo) === n) : undefined;
  }, [codigo, variaveis]);

  async function salvar() {
    let vals: any;
    try { vals = await form.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      const r = await api('op_variavel_criar', {
        codigo: String(vals.codigo).trim(),
        grandeza_codigo: vals.grandeza_codigo,
        rotulo: vals.rotulo,
        unidade: vals.unidade,
        faixa_min: vals.faixa_min ?? null,
        faixa_max: vals.faixa_max ?? null,
        resolucao: vals.resolucao ?? null,
        acumulada: !!vals.acumulada,
        intervalo_seg: vals.intervalo_seg ?? null,
        diagnostico: !!vals.diagnostico,
        observacao: vals.observacao || null,
      });
      message.success(`Variável "${r.variavel.codigo}" cadastrada.`);
      form.resetFields(); setCodigo(''); setGrandeza(undefined);
      onCriada(r.variavel);
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  return (
    <Modal open={aberto} onCancel={onFechar} onOk={salvar} okText="Cadastrar"
      confirmLoading={salvando} title="Cadastrar variável nova"
      width="94vw" style={{ maxWidth: 720, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' } }}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="A grandeza é o que permite comparar estações"
        description="O nome é livre, a grandeza física não. Duas estações só são comparáveis se falam da mesma grandeza — por isso ela é obrigatória, junto com a faixa que barra leitura impossível." />

      <Form form={form} layout="vertical" size="small"
        initialValues={{ codigo: nomeInicial || '', diagnostico: false, acumulada: false }}>
        <Space align="start" wrap style={{ width: '100%' }} size={12}>
          <Form.Item name="codigo" label="Nome (como aparece nos dados)" required
            style={{ minWidth: 260 }}
            rules={[
              { required: true, message: 'Informe o nome' },
              { pattern: /^[A-Za-z][A-Za-z0-9_]{1,39}$/, message: 'Comece com letra; só letras, números e _' },
              { validator: () => colisao ? Promise.reject(new Error(`Já existe "${colisao.codigo}"`)) : Promise.resolve() },
            ]}>
            <Input placeholder="TensaoBateria" onChange={e => setCodigo(e.target.value)} />
          </Form.Item>
          <Form.Item name="grandeza_codigo" label="Grandeza física" required style={{ minWidth: 220 }}
            rules={[{ required: true, message: 'Escolha a grandeza' }]}>
            <Select placeholder="Escolha…" options={grandezas.map(x => ({ value: x.codigo, label: `${x.rotulo} (${x.unidade_canonica || '—'})` }))}
              onChange={(v) => {
                setGrandeza(v);
                const gg = grandezas.find(x => x.codigo === v);
                if (gg) form.setFieldsValue({ unidade: gg.unidade_canonica });
              }} />
          </Form.Item>
          <Form.Item name="unidade" label="Unidade" style={{ width: 110 }}>
            <Input placeholder="m" />
          </Form.Item>
        </Space>

        <Form.Item name="rotulo" label="Rótulo (como aparece na tela)">
          <Input placeholder="Tensão da bateria interna" />
        </Form.Item>

        {colisao && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message={`"${colisao.codigo}" já existe e é o mesmo nome ignorando acento e maiúscula`}
            description="Use a variável existente em vez de criar uma quase-igual — foi assim que a base ficou com Nivel e nivel separados." />
        )}

        {!semFaixa && (
          <>
            <Space align="start" wrap size={12}>
              <Form.Item name="faixa_min" label="Mínimo físico" required
                rules={[{ required: true, message: 'Obrigatório' }]}>
                <InputNumber decimalSeparator="," style={{ width: 150 }}
                  placeholder={g?.limite_min != null ? String(g.limite_min) : 'mín'} />
              </Form.Item>
              <Form.Item name="faixa_max" label="Máximo físico" required
                rules={[
                  { required: true, message: 'Obrigatório' },
                  ({ getFieldValue }) => ({
                    validator: (_, v) => (v == null || getFieldValue('faixa_min') == null || v > getFieldValue('faixa_min'))
                      ? Promise.resolve() : Promise.reject(new Error('Tem que ser maior que o mínimo')),
                  }),
                ]}>
                <InputNumber decimalSeparator="," style={{ width: 150 }}
                  placeholder={g?.limite_max != null ? String(g.limite_max) : 'máx'} />
              </Form.Item>
              <Form.Item name="resolucao" label="Passo do sensor (opcional)"
                tooltip="Báscula de 0,2 mm; tensão do LogBox 0,01 V. Serve de teste: valores que não são múltiplos disto denunciam que o modelo é outro.">
                <InputNumber decimalSeparator="," style={{ width: 170 }} placeholder="0,01" />
              </Form.Item>
            </Space>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: -8, marginBottom: 12 }}>
              Faixa do <b>impossível</b>, não do improvável: fora dela a leitura é inválida e não entra na série.
              {g?.limite_min != null && ` A grandeza ${g.rotulo} aceita de ${g.limite_min} a ${g.limite_max}.`}
            </Text>
          </>
        )}

        {acumulavel && (
          <Space align="start" wrap size={12}>
            <Form.Item name="acumulada" label="É acumulado" valuePropName="checked">
              <Switch size="small" />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.acumulada !== b.acumulada}>
              {({ getFieldValue }) => getFieldValue('acumulada') ? (
                <Form.Item name="intervalo_seg" label="Janela de acumulação (segundos)" required
                  tooltip="Sem isto ninguém sabe se 12 mm são de 15 minutos ou de 1 hora — e somar as duas conta a mesma chuva duas vezes."
                  rules={[{ required: true, message: 'Obrigatório para acumulado' }]}>
                  <InputNumber style={{ width: 200 }} placeholder="900 (15 min) · 3600 (1 h)" />
                </Form.Item>
              ) : null}
            </Form.Item>
          </Space>
        )}

        <Form.Item name="diagnostico" label="É dado de diagnóstico do equipamento" valuePropName="checked"
          tooltip="Bateria, temperatura da placa, GPS. Marcado, nunca vira manchete de card nem série no portal do cliente — foi a falta disso que fez 14,11 V aparecer como metro de água.">
          <Switch size="small" />
        </Form.Item>

        <Form.Item name="observacao" label="Observação (opcional)">
          <Input.TextArea rows={2} placeholder="De onde veio a faixa: manual do fabricante, página, tabela…" />
        </Form.Item>
      </Form>

      <Text type="secondary" style={{ fontSize: 11 }}>
        A variável nasce como <b>proposta</b>: funciona igual às demais (grava, plota, exporta) e só entra
        numa fila de revisão para alguém confirmar o rótulo depois.
      </Text>
    </Modal>
  );
}
